import { useState, useCallback, useEffect, useRef } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "@/routing/navigation";
import { AudienceStep } from "./AudienceStep";
import { AudienceFilterModal } from "./AudienceFilterModal";
import { CreationFlowStickyActionBar } from "./CreationFlowStickyActionBar";
import { isRelaunchingStudyAudienceStatus, isSelectableStudyAudienceStatus } from "@/lib/audience-selectability";
import { RelaunchingAudienceDialog } from "@/components/audiences/RelaunchingAudienceDialog";
import { resolveAudienceCanonicalCoverUrl } from "@/pages/AudiencesPage/utils/audience-presentation";
import type { Population } from "@/types/routes/tests";
import type { PopulationFilter } from "@/types/routes/test-results";
import type { AudienceFilterLabel, SelectedSegment } from "@digital-twins/shared-types";

interface AudienceStepWrapperProps {
  loading: boolean;
  loaded: boolean;
  audienceLoadError: boolean;
  populations: Population[];
  onFilterApply: (
    populationId: string,
    filter: PopulationFilter,
    filteredCount: number,
    selectedSegments?: Array<SelectedSegment>,
    filterLabels?: Array<AudienceFilterLabel>,
  ) => void;
  onContinueWithout: (populationId: string) => void;
  onRetryAudienceLoad: () => void;
  /**
   * When the parent owns relaunch confirmation, pass the population it has
   * already committed. A relaunching deep link waits until that id matches
   * before the filter modal opens. Omit the prop and this step shows the
   * same dialog itself.
   */
  confirmedPopulationId?: string;
  onBack?: () => void;
  backLabel?: string;
  /** When set, opens the filter modal for this population as soon as data is loaded (once). */
  defaultOpenForPopulationId?: string | null;
}

export function AudienceStepWrapper({
  loading,
  loaded,
  audienceLoadError,
  populations,
  onFilterApply,
  onContinueWithout,
  onRetryAudienceLoad,
  confirmedPopulationId,
  onBack,
  backLabel,
  defaultOpenForPopulationId,
}: AudienceStepWrapperProps) {
  const { t } = useTranslation("app");
  const resolvedBackLabel = backLabel ?? t("creation.changeGoal");
  const searchParams = useSearchParams();
  const [pendingPopulationId, setPendingPopulationId] = useState<string | null>(
    null,
  );
  const [pendingAutoRelaunchId, setPendingAutoRelaunchId] = useState<string | null>(
    null,
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const deepLinkModalHandledRef = useRef(false);
  const defaultOpenHandledRef = useRef(false);

  const readyPopulations = populations.filter((p) =>
    isSelectableStudyAudienceStatus(p.status),
  );

  const openFilterModalForPopulation = useCallback((id: string) => {
    setPendingPopulationId(id);
    setIsFilterModalOpen(true);
  }, []);

  const beginAutoOpen = useCallback((population: Population): boolean => {
    if (
      isRelaunchingStudyAudienceStatus(population.status) &&
      population.id !== confirmedPopulationId
    ) {
      if (confirmedPopulationId === undefined) {
        setPendingAutoRelaunchId(population.id);
        return true;
      }
      return false;
    }
    openFilterModalForPopulation(population.id);
    return true;
  }, [confirmedPopulationId, openFilterModalForPopulation]);

  const onAudienceClick = useCallback(
    (id: string) => {
      openFilterModalForPopulation(id);
    },
    [openFilterModalForPopulation],
  );

  // Home page "Start study" deep-links ?audience=… — show filter modal on step 1
  // instead of skipping straight to the questionnaire step.
  useEffect(() => {
    if (!loaded || deepLinkModalHandledRef.current) {
      return;
    }
    const audienceParam =
      searchParams.get("audience") || searchParams.get("populationId");
    if (!audienceParam) {
      return;
    }
    const population = readyPopulations.find((p) => p.id === audienceParam);
    if (!population) {
      return;
    }
    if (!beginAutoOpen(population)) {
      return;
    }
    deepLinkModalHandledRef.current = true;
  }, [loaded, searchParams, readyPopulations, beginAutoOpen]);

  // Duplicate "Change audience" flow — open filter modal for the already-selected
  // population so the user can pick a different audience or confirm the existing one.
  useEffect(() => {
    if (!loaded || !defaultOpenForPopulationId || defaultOpenHandledRef.current) {
      return;
    }
    const population = readyPopulations.find((p) => p.id === defaultOpenForPopulationId);
    if (!population) return;
    if (!beginAutoOpen(population)) {
      return;
    }
    defaultOpenHandledRef.current = true;
  }, [loaded, defaultOpenForPopulationId, readyPopulations, beginAutoOpen]);

  const handleFilterApply = useCallback(
    (
      filter: PopulationFilter,
      filteredCount: number,
      // The tag names are the segments' labels: the segments carry them.
      _appliedTagNames?: Array<string>,
      selectedSegments?: Array<SelectedSegment>,
      filterLabels?: Array<AudienceFilterLabel>,
    ) => {
      if (!pendingPopulationId) return;
      setIsFilterModalOpen(false);
      onFilterApply(
        pendingPopulationId,
        filter,
        filteredCount,
        selectedSegments,
        filterLabels,
      );
      setPendingPopulationId(null);
    },
    [pendingPopulationId, onFilterApply],
  );

  const handleContinueWithout = useCallback(() => {
    if (!pendingPopulationId) return;
    setIsFilterModalOpen(false);
    onContinueWithout(pendingPopulationId);
    setPendingPopulationId(null);
  }, [pendingPopulationId, onContinueWithout]);

  const handleModalOpenChange = useCallback((open: boolean) => {
    setIsFilterModalOpen(open);
    if (!open) setPendingPopulationId(null);
  }, []);

  const pendingPopulation = pendingPopulationId
    ? (populations.find((p) => p.id === pendingPopulationId) ?? null)
    : null;

  return (
    <div key="step1" className="flex-1 min-h-0 flex flex-col">
      {audienceLoadError ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <p className="text-sm text-destructive">
              {t("creation.loadAudiencesFailed")}
            </p>
            <Button variant="outline" size="sm" onClick={onRetryAudienceLoad}>
              {t("creation.askAi.retry")}
            </Button>
          </div>
        </div>
      ) : loading && !loaded ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <AudienceStep
            populations={populations}
            onAudienceClick={onAudienceClick}
          />

          {onBack ? (
            <CreationFlowStickyActionBar
              start={
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onBack}
                  data-testid="study-wizard-back-button"
                  className="h-8 rounded-md px-3 text-[13px] font-medium text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                  {resolvedBackLabel}
                </Button>
              }
            />
          ) : null}

          <RelaunchingAudienceDialog
            open={pendingAutoRelaunchId !== null}
            onCancel={() => setPendingAutoRelaunchId(null)}
            onConfirm={() => {
              if (!pendingAutoRelaunchId) {
                return;
              }
              openFilterModalForPopulation(pendingAutoRelaunchId);
              setPendingAutoRelaunchId(null);
            }}
          />

          {pendingPopulation && (
            <AudienceFilterModal
              key={pendingPopulation.id}
              open={isFilterModalOpen}
              onOpenChange={handleModalOpenChange}
              populationId={pendingPopulation.id}
              populationName={pendingPopulation.name}
              coverImageUrl={resolveAudienceCanonicalCoverUrl(
                pendingPopulation,
              )}
              prefetchedData={null}
              audienceType={pendingPopulation.audienceType ?? null}
              onFilterApply={handleFilterApply}
              onContinueWithout={handleContinueWithout}
              audienceTags={pendingPopulation.audienceTags}
              forStudy
            />
          )}
        </>
      )}
    </div>
  );
}
